/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as R from 'ramda';
import PhotoBooth from '../../webcam/PhotoBooth';
import ModalView, { ModalContent } from '../../layout/modal/ModalView';
import camera from '../../assets/icons/camera.svg';
import { css } from '@emotion/react';
import { INPUT_WIDTH } from '../../webcam/constants';
import { ellipsizeAtSecondLine } from '../guestCard/guestCardStyles';
import { MODAL_SIZE } from '../../layout/modal/modalSize';
import { GuestDetails } from '../guestDetails';
import { connect } from 'react-redux';
import { isNilOrEmpty } from '../../language/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { IAppState } from '../../core/redux/IAppState';
import { getLoading } from '../../core/redux/api/module';
import { getGuestPhotoAction } from './getGuestPhotoAction';
import { setGuestPhotoAction } from './setGuestPhotoAction';
import LabeledData from '../../forms/elements/LabeledData';
import { fullName } from '../module';
import ModalTitleHeader from '../../layout/modal/ModalTitleHeader';
import { getResource } from '../../language/resources/getResource';

const GuestPhotoLabel = css`
  max-width: ${INPUT_WIDTH}px;
  margin: 0 auto 20px;
  ${ellipsizeAtSecondLine};
`

type Props = {
  guestId: string;
  guest?: GuestDetails;
  isLoading?: boolean;
  resources: Record<string, any>;
  onDismiss: () => void;
  setPhoto?: (guest: GuestDetails, img: string) => void;
  getPhoto?: (guest: GuestDetails, cloudinaryUrl: string) => void;
};


/**
 * PassPhotoModal: a component that renders a modal containing the photo capture "form" (form-like flow). You can give
 * it an image in, in which case the user will start off looking at the pre-supplied image. Otherwise, you can pass
 * nothing and the form will start in image capture mode. This does render a modal, so when you don't want the modal
 * shown anymore, you have to remove this whole component from the DOM via conditional rendering.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState, props: Props) => ({
    guest:     state.guests.get(props.guestId),
    isLoading: !!getLoading(state, props.guestId, ACTION_TYPE.GET_GUEST_PHOTO_REQUEST)
               || !!getLoading(state, props.guestId, ACTION_TYPE.SET_GUEST_PHOTO_REQUEST),
    resources: state.locale
  }),
  (dispatch) => ({
    getPhoto: (guest: GuestDetails, cloudinaryUrl: string) =>
                dispatch(getGuestPhotoAction(guest, cloudinaryUrl, 'large')),
    setPhoto: (guest: GuestDetails, img: string) => dispatch(setGuestPhotoAction(guest, img)),
  }),
)(class PassPhotoModal extends React.PureComponent<Props> {
  public componentDidMount(): void {
    if (!R.path([ 'guest', 'photo', 'imgLarge' ], this.props) // Don't have an image
        && R.path([ 'guest', 'photo', 'largePhotoUrl' ], this.props) // Have a URL
    ) {
      this.props.getPhoto!(this.props.guest!, this.props.guest!.photo.largePhotoUrl);
    }
  }


  public onImageSubmit = (base64Image: string | null | undefined) => {
    if (isNilOrEmpty(base64Image)) {
      this.props.onDismiss();
      return;
    }

    const foo = <button />;
    console.log(foo);

    this.props.setPhoto!(this.props.guest!, base64Image!);

    this.props.onDismiss();
  };


  public render = () => (
    <ModalView onDismiss={this.props.onDismiss}
               rootCss={css`text-align: center;`}
               contentCss={css`margin-top: 0;`}
               size={MODAL_SIZE.MEDIUM}>
      <ModalTitleHeader resources={this.props.resources} heading={getResource(this.props.resources, 'GUEST_PHOTO_MODAL_HEADING')} icon={camera} onDismiss={this.props.onDismiss} />
      <ModalContent>
        <LabeledData label={getResource(this.props.resources, 'GUEST_PHOTO_GUEST_LABEL')}
                    data={fullName(this.props.guest)}
                    rootCss={GuestPhotoLabel}/>
        <PhotoBooth
          resources={this.props.resources}
          isLoading={!!this.props.isLoading}
          image={
            R.path([ 'guest', 'photo', 'imgLarge' ], this.props)
            || R.path([ 'guest', 'photo', 'imgSmall' ], this.props)
          }
          onImageSubmit={this.onImageSubmit}
          rootCss={css`margin: 0 20px;`}/>
      </ModalContent>
    </ModalView>
  );
});
